import React, { useEffect, useState } from "react";
import { getDeviceOtaLogView } from "../../lib/api";
import { PageLoader } from "../../components/ui/spinner";
import { EventsPagination, PAGE_SIZE, downloadEventsCsv } from "../activitySessions/sessionLogShared";

/**
 * OTA Log tab — the logs the device files around a firmware update.
 *
 * The device sends two: one just BEFORE the update starts and one just AFTER it
 * finishes (POST /device/ota-logs, once per phase). This panel shows the pair
 * for whichever update is selected in the FW History rail on the left, behind a
 * Before / After switch.
 *
 * Why the rail drives it: an OTA log only means something next to the update it
 * belongs to, and that rail already selects an update_id. The list of logs for
 * the selected update is fetched by DeviceDetailPage alongside the audit and app
 * logs; this component fetches only the CONTENT of the phase being looked at, so
 * opening the tab never pulls both files when one is being read.
 *
 * Two render shapes, because the service serves two. A log that is the device's
 * CSV export arrives parsed into events and renders as the same
 * TIMESTAMP / EVENT / DETAIL table the session-log pages use. Anything else
 * arrives as raw text and renders verbatim — a log in an unexpected format is
 * still readable rather than showing as an empty table.
 */

const PHASES = [
  { key: "before", label: "Before Update" },
  { key: "after", label: "After Update" },
];

function phaseRow(otaLogs, phase) {
  return (otaLogs || []).find((row) => row.phase === phase) || null;
}

function formatUtc(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-GB", { dateStyle: "short", timeStyle: "medium", timeZone: "UTC" });
}

/** The dark info strip above the log: which update this is, and the versions it moved between. */
function OtaLogSummary({ selectedHistory, otaLogs }) {
  const anyRow = (otaLogs || [])[0] || {};
  const fields = [
    { label: "UPDATE ID", value: selectedHistory?.update_id || anyRow.updateId || "-" },
    { label: "FROM VERSION", value: anyRow.deviceBaseline || "-" },
    { label: "TO VERSION", value: anyRow.fwVersion || selectedHistory?.version || "-" },
    { label: "LOGS RECORDED", value: `${(otaLogs || []).length} of 2` },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 bg-[#08148C] rounded-lg px-5 py-4 mb-4">
      {fields.map((f) => (
        <div key={f.label}>
          <p className="text-[10px] font-semibold text-blue-200 uppercase tracking-wider mb-1">{f.label}</p>
          <p className="text-sm font-mono text-white break-all">{f.value}</p>
        </div>
      ))}
    </div>
  );
}

/** The Before / After switch. A phase the device never filed is shown, but disabled. */
function PhaseSwitch({ otaLogs, phase, onSelect }) {
  return (
    <div className="flex items-center gap-2">
      {PHASES.map((p) => {
        const available = Boolean(phaseRow(otaLogs, p.key));
        const active = phase === p.key;
        let className = "bg-white text-slate-600 border-slate-300 hover:border-[#08148C]";
        if (active) className = "bg-[#08148C] text-white border-[#08148C]";
        else if (!available) className = "bg-slate-50 text-slate-300 border-slate-200 cursor-not-allowed";
        return (
          <button
            key={p.key}
            type="button"
            disabled={!available}
            aria-pressed={active}
            onClick={() => onSelect(p.key)}
            className={`text-xs font-semibold px-3 py-1.5 rounded-md border transition-colors ${className}`}
          >
            {p.label}
          </button>
        );
      })}
    </div>
  );
}

function EventsTable({ events }) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider border-b border-slate-100">
          <th className="px-3 py-2">Timestamp (ms)</th>
          <th className="px-3 py-2">Event</th>
          <th className="px-3 py-2">Detail</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-50">
        {events.map((e, i) => (
          <tr key={i} /* NOSONAR S6479: raw log rows carry no unique id; a composite key risks duplicates. */ className="hover:bg-slate-50 transition-colors">
            <td className="px-3 py-2.5 font-mono text-xs text-slate-500 whitespace-nowrap">{e.ts}</td>
            <td className="px-3 py-2.5 font-mono text-xs text-slate-700">{e.event}</td>
            <td className="px-3 py-2.5 text-slate-600">{e.detail}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** The loaded log itself: parsed events with pagination, or the raw file. */
function LogBody({ logView, page, setPage }) {
  if (logView.format === "text") {
    return (
      <pre className="text-xs font-mono text-slate-600 bg-slate-50 border border-slate-100 rounded-lg p-4 overflow-x-auto whitespace-pre-wrap break-words max-h-[32rem]">
        {logView.content}
      </pre>
    );
  }

  const allEvents = logView.events;
  if (allEvents.length === 0) {
    return <p className="text-xs text-slate-400 py-6 text-center">This log file contains no events.</p>;
  }

  const totalPages = Math.max(1, Math.ceil(allEvents.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const events = allEvents.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <>
      <EventsTable events={events} />
      <EventsPagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={allEvents.length}
        onPageChange={setPage}
      />
    </>
  );
}

export default function OtaLogTabPanel({
  serial,
  selectedHistory,
  otaLogs,
  otaLogsLoading,
  otaLogsError,
  canDownload,
  setAccessDeniedOpen,
  noUpdates,
}) {
  const [phase, setPhase] = useState("before");
  const [logView, setLogView] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewError, setViewError] = useState(null);
  const [page, setPage] = useState(1);

  // Land on a phase the device actually filed. An update that failed before it
  // started has only a "before" log; one recorded before this feature shipped
  // may have only an "after".
  useEffect(() => {
    if (!otaLogs || otaLogs.length === 0) return;
    setPhase((current) => (phaseRow(otaLogs, current) ? current : otaLogs[0].phase));
  }, [otaLogs]);

  const row = phaseRow(otaLogs, phase);
  const logId = row?.logId || null;

  // Only the phase being read is fetched, not both.
  useEffect(() => {
    if (!logId) {
      setLogView(null);
      return;
    }
    let cancelled = false;
    setLogView(null);
    setViewError(null);
    setViewLoading(true);
    setPage(1);
    getDeviceOtaLogView(serial, logId)
      .then((data) => {
        if (!cancelled) setLogView(data);
      })
      .catch((err) => {
        if (!cancelled) setViewError(err.message || "Failed to load the OTA log");
      })
      .finally(() => {
        if (!cancelled) setViewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [serial, logId]);

  function handleDownload() {
    // 003 Role Gate: Nurse and Patient may read this page but never receive a file.
    if (!canDownload) {
      setAccessDeniedOpen(true);
      return;
    }
    const name = `${serial}_ota-${phase}_${logView.updateId || logView.logId}`;
    if (logView.format === "text") {
      const blob = new Blob([logView.content], { type: "text/plain;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${name}.txt`;
      a.click();
      URL.revokeObjectURL(url);
      return;
    }
    downloadEventsCsv(`${name}.csv`, logView.events);
  }

  if (!selectedHistory) {
    return (
      <div className="pt-2">
        <p className="text-xs text-slate-400 py-6 text-center">
          {noUpdates
            ? "No firmware update is recorded for this device, so there are no OTA logs to show."
            : "Click a FW History item on the left to view the logs the device filed before and after that update."}
        </p>
      </div>
    );
  }

  return (
    <div className="pt-2">
      {otaLogsLoading && <PageLoader />}
      {otaLogsError && <p className="text-xs text-red-500 py-4">{otaLogsError}</p>}

      {!otaLogsLoading && !otaLogsError && otaLogs !== null && (
        <>
          {otaLogs.length === 0 ? (
            <p className="text-xs text-slate-400 py-6 text-center">
              No OTA logs were recorded for this update. A device that updated before OTA logging
              was introduced, or one that never reported, will have none.
            </p>
          ) : (
            <>
              <OtaLogSummary selectedHistory={selectedHistory} otaLogs={otaLogs} />

              <div className="flex items-center justify-between gap-4 mb-3">
                <PhaseSwitch otaLogs={otaLogs} phase={phase} onSelect={setPhase} />
                {logView && (
                  <button
                    type="button"
                    onClick={handleDownload}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold border border-[#08148C] text-[#08148C] rounded px-3 py-1.5 hover:bg-[#08148C] hover:text-white transition-colors"
                  >
                    &#8659; Download log
                  </button>
                )}
              </div>

              {row && (
                <p className="text-xs text-slate-400 mb-3">
                  Device clock: {formatUtc(row.loggedAt)} UTC &middot; received {formatUtc(row.receivedTime)} UTC
                </p>
              )}

              {viewLoading && <PageLoader />}
              {viewError && <p className="text-xs text-red-500 py-4">{viewError}</p>}
              {!viewLoading && !viewError && logView && (
                <LogBody logView={logView} page={page} setPage={setPage} />
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
